import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { I18N_KEYS } from '@/language/keys';
import { goToRecipe } from '@/utils/recipes';
import { toMillis } from '@/utils/folder.utils';
import { useGetUserRecipes } from '@/queries/useRecipes';
import type { DashboardRecipe } from '@/types/recipes';

interface OpenRecentMenuItemProps {
  currentRecipeId: string;
  onClose: () => void;
}

export const OpenRecentMenuItem = ({ currentRecipeId, onClose }: OpenRecentMenuItemProps) => {
  const { t } = useTranslation();
  const { data: recipes = [], isLoading: isLoadingUserRecipes } = useGetUserRecipes();

  const sortedRecipes: DashboardRecipe[] = useMemo(() => {
    return recipes
      .filter((recipe) => recipe.id !== currentRecipeId)
      .sort((a, b) => {
        // Get the effective updated timestamp for each item
        const timestampA: number = Math.max(toMillis(a.updatedAt), toMillis(a.createdAt));
        const timestampB: number = Math.max(toMillis(b.updatedAt), toMillis(b.createdAt));

        // Sort by timestamp descending (newest first)
        return timestampB - timestampA;
      })
      .slice(0, 5);
  }, [recipes, currentRecipeId]);

  return (
    <ParentMenuItem
      id="open-recent"
      label={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.OPEN_RECENT)}
      onClose={onClose}
      contextMenuWidth="180px"
      offset={{ left: '10px', top: '-9px' }}
      disabled={sortedRecipes.length === 0 || isLoadingUserRecipes}
    >
      {sortedRecipes.map((recipe, index) => (
        <RecipeMenuItem key={recipe.id} recipe={recipe} isLast={index === sortedRecipes.length - 1} />
      ))}
    </ParentMenuItem>
  );
};

const RecipeMenuItem = ({ recipe, isLast }: { recipe: DashboardRecipe; isLast: boolean }) => {
  const handleClick = useCallback(() => {
    goToRecipe(recipe.id, true);
  }, [recipe.id]);

  return (
    <AppMenuItem onClick={handleClick} sx={{ mb: isLast ? 0 : 0.25, py: 0.5 }}>
      <EllipsisText sx={{ flex: 1 }} variant="body-sm-rg">
        {recipe.name}
      </EllipsisText>
    </AppMenuItem>
  );
};
